// This service uses EmailJS to send emails directly from the client-side
// Learn more: https://www.emailjs.com/
import emailjs from '@emailjs/browser'

interface SendEmailParams {
  to: string
  subject: string
  html: string
}

// EmailJS configuration
// Replace these with your actual EmailJS credentials
const EMAIL_CONFIG = {
  PUBLIC_KEY: 'user_your_actual_public_key',
  SERVICE_ID: 'service_maestroconnect',
  TEMPLATE_ID: 'template_verification',
  MAX_RETRIES: 2,
  RETRY_DELAY: 2000, // 2 seconds
}

// Initialize EmailJS with your public key
// This should be called once when your app starts
export const initEmailService = () => {
  try {
    emailjs.init(EMAIL_CONFIG.PUBLIC_KEY)
    console.log('EmailJS initialized successfully')
  } catch (error) {
    console.error('Failed to initialize EmailJS:', error)
  }
}

// Helper function to wait for a specified time
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Function to send a generic email
export const sendEmail = async ({
  to,
  subject,
  html,
}: SendEmailParams): Promise<boolean> => {
  try {
    // Send email using EmailJS
    const response = await emailjs.send(
      EMAIL_CONFIG.SERVICE_ID,
      EMAIL_CONFIG.TEMPLATE_ID,
      {
        to_email: to,
        subject: subject,
        message_html: html,
        // Any other template variables you need
      },
    )
    console.log('Email sent successfully:', response)
    return response.status === 200
  } catch (error) {
    console.error('Error sending email:', error)
    return false
  }
}

// NOTE: We are now using Firebase's built-in email verification
// This function is kept for backward compatibility but is no longer
// used for verification emails
export const sendVerificationEmail = async (
  to: string,
  verificationCode: string,
): Promise<{ success: boolean; error?: string }> => {
  console.warn(
    'Using EmailJS for verification is deprecated. Using Firebase Authentication instead.',
  )
  try {
    // Log that we're falling back to EmailJS
    console.log('Falling back to EmailJS for verification email')
    // When using EmailJS templates, the template is defined in your EmailJS account
    // Just pass the necessary variables
    const response = await emailjs.send(
      EMAIL_CONFIG.SERVICE_ID,
      EMAIL_CONFIG.TEMPLATE_ID,
      {
        to_email: to,
        verification_code: verificationCode,
        // Make sure these match exactly with your EmailJS template variables
        recipient: to,
        code: verificationCode,
      },
    )
    if (response.status === 200) {
      console.log('Verification email sent successfully via EmailJS:', response)
      return { success: true }
    } else {
      throw new Error(`EmailJS returned status ${response.status}`)
    }
  } catch (error) {
    console.error('Error sending verification email via EmailJS:', error)
    let errorMessage = 'Unknown error occurred'
    if (error instanceof Error) {
      errorMessage = error.message
    } else if (typeof error === 'string') {
      errorMessage = error
    }
    // Check for common errors and provide more helpful messages
    if (
      errorMessage.includes('Network Error') ||
      errorMessage.includes('timeout')
    ) {
      errorMessage = 'Network error. Please check your internet connection.'
    } else if (errorMessage.includes('Invalid API key')) {
      errorMessage =
        'Email service configuration error. Please contact support.'
    }
    return {
      success: false,
      error: errorMessage,
    }
  }
}
